import { useNavigate, useParams } from 'react-router';
import { RANK_IDS } from '@rw/shared';
import { Page } from '../../components/Layout';
import { QueryView } from '../../components/states';
import { Card } from '../../components/ui';
import { useBadges, usePlayerStats } from '../../hooks/queries';
import { useAuth } from '../../stores/auth';
import { useT } from '../../i18n';
import { BadgeGrid } from './BadgeGrid';
import { RANK_STYLE, rankMinLevel, rankName, rankOf } from './achievements';

/** /badges (mine) and /players/:id/badges: the rank ladder and every badge. */
export function BadgesPage() {
  const { id } = useParams<{ id: string }>();
  const me = useAuth((s) => s.user?.id);
  const isMe = !id || id === me;
  const badges = useBadges(isMe ? null : id!);
  const stats = usePlayerStats(isMe ? (me ?? '') : id!).data;
  const navigate = useNavigate();
  const t = useT();
  const level = stats?.progress.level ?? 1;
  const current = rankOf(level);
  return (
    <Page>
      <button onClick={() => navigate(-1)} className="mb-2 text-sm font-semibold text-ink-300">
        {t('← Back')}
      </button>
      <h1 className="font-display text-3xl font-bold">
        {isMe ? t('My badges') : t('Badges of {name}', { name: stats?.user.displayName ?? '' })}
      </h1>

      <Card className="mt-4">
        <h2 className="text-sm font-semibold text-ink-300">{t('Rank tier')}</h2>
        <ol className="mt-3 grid grid-cols-4 gap-2" data-testid="rank-ladder">
          {RANK_IDS.map((r) => {
            const s = RANK_STYLE[r];
            const reached = level >= rankMinLevel(r);
            return (
              <li
                key={r}
                className={`flex flex-col items-center rounded-xl px-1 py-2 text-center ${r === current ? 'ring-2 ring-volt' : ''} ${reached ? '' : 'opacity-40'}`}
                data-current={r === current || undefined}
              >
                <span
                  className="hex-clip grid size-9 place-items-center text-lg"
                  style={{ background: s.bg }}
                >
                  {s.icon}
                </span>
                <span className="mt-1 text-[11px] font-semibold leading-tight">{rankName(r)}</span>
                <span className="text-[10px] text-ink-500">
                  {t('Lv {n}+', { n: rankMinLevel(r) })}
                </span>
              </li>
            );
          })}
        </ol>
        <p className="mt-3 text-xs text-ink-400">
          {t('Your rank follows your level: earn XP by running and capturing sectors to climb.')}
        </p>
      </Card>

      <div className="mt-5">
        <QueryView query={badges}>
          {(b) => (
            <>
              <p className="mb-3 text-sm text-ink-400" data-testid="badge-count">
                {t('{n} badge tiers unlocked', { n: b.earnedCount })}
              </p>
              <BadgeGrid badges={b.badges} isMe={isMe} />
            </>
          )}
        </QueryView>
      </div>
    </Page>
  );
}
